// ======================================================================
// File: src/modules/ipe/engineering/bom/BOMLinesTable.js
// ======================================================================

import React, { useEffect, useState } from "react";
import ipeEnquiryService from "../../services/ipeEnquiryService";
import axiosClient from "../../../../api/axiosClient";
import BOMMaterialSelectorModal from "./BOMMaterialSelectorModal"; // ✅ NEW

export default function BOMLinesTable({ lines, bomId, canEdit = true, onReload, materialMap  }) {

  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingLineId, setEditingLineId] = useState(null);

  const [rawMaterials, setRawMaterials] = useState([]);
  const [boughtOutItems, setBoughtOutItems] = useState([]);

  const [showSelector, setShowSelector] = useState(false); // ✅ NEW
  
  // 🔥 CHILD MATERIAL FLOW
  const [childProcessType, setChildProcessType] = useState(null);
  const [childParentLine, setChildParentLine] = useState(null);
  // 🔥 PER ROW SAVING
  const [savingLineId, setSavingLineId] = useState(null);

  const [form, setForm] = useState({
    material_type: "RAW",
    raw_material_id: "",
    bought_out_item_id: "",
    uom_id: "",
    uom: "",
    quantity: "",
    scrap_percent: "",
    remarks: "",
  });

  const [editForm, setEditForm] = useState({
    quantity: "",
    scrap_percent: "",
    remarks: "",
    painting_required: false,
    insulation_required: false,
    fabrication_required: false,
  });

  // ------------------------------------------------------------
  // Load masters
  // ------------------------------------------------------------
  useEffect(() => {

    async function loadMasters() {

      try {

        const [rawRes, boughtRes] = await Promise.all([
          axiosClient.get("/api/ppe/masters/raw-materials"),
          axiosClient.get("/api/ppe/masters/boughtout-items"),
        ]);

        setRawMaterials(rawRes.data?.data || []);
        setBoughtOutItems(boughtRes.data?.data || []);

      } catch (err) {
        console.error("Failed to load material masters", err);
      }

    }

    loadMasters();

  }, []);

  // ------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------

  //function resolveMaterialName(line) {
  //  if (line.material_name) return line.material_name;
  //  if (line.material_id) return `ID: ${line.material_id}`;
  //  return "-";
  //}
  function resolveMaterialName(line) {
  return (
    line.material_name ||
    materialMap?.[String(line.material_id)] ||
    `ID: ${line.material_id}`
  );
}
  function resolveManufacturer(line) {
    return line.manufacturer || "-"; // placeholder for now
  }

  function handleMaterialSelect(type, id) {

    if (!id) return;

    if (type === "RAW") {

      const m = rawMaterials.find(r => r.id === Number(id));

      setForm({
        ...form,
        raw_material_id: id,
        uom_id: m?.uom_id || "",
        uom: m?.uom || "",
      });

    } else {

      const b = boughtOutItems.find(i => i.id === Number(id));

      setForm({
        ...form,
        bought_out_item_id: id,
        uom_id: b?.uom_id || "",
        uom: b?.uom || "",
      });

    }
  }

  // ------------------------------------------------------------
  // Add Line (UNCHANGED)
  // ------------------------------------------------------------
  async function handleAddLine() {

    if (!bomId || !canEdit) return;

    if (!form.quantity) {
      alert("Quantity required");
      return;
    }

    setSaving(true);
    //setSavingLineId(lineId);

    try {

      await ipeEnquiryService.createBOMLine({

        bom_header_id: bomId,

        material_type: form.material_type,

        raw_material_id:
          form.material_type === "RAW"
            ? form.raw_material_id
            : null,

        bought_out_item_id:
          form.material_type === "BOUGHT_OUT"
            ? form.bought_out_item_id
            : null,

        uom_id: form.uom_id,
        quantity: Number(form.quantity),
        scrap_percent: Number(form.scrap_percent || 0),
        remarks: form.remarks,

      });

      setForm({
        material_type: "RAW",
        raw_material_id: "",
        bought_out_item_id: "",
        uom: "",
        quantity: "",
        scrap_percent: "",
        remarks: "",
      });

      setAdding(false);

      onReload && onReload();

    } catch (err) {

      console.error("Failed to add BOM line", err);

    } finally {

      setSaving(false);
      //setSavingLineId(null);

    }

  }

  // ------------------------------------------------------------
  // Edit Line (UNCHANGED)
  // ------------------------------------------------------------
  function startEdit(line) {

    if (!canEdit) return;

    setEditingLineId(line.id);

    setEditForm({
      quantity: line.quantity,
      scrap_percent: line.scrap_percent || 0,
      remarks: line.remarks || "",
      painting_required: !!line.painting_required,
      insulation_required: !!line.insulation_required,
      fabrication_required: !!line.fabrication_required,
    });

  }

  async function saveEdit(lineId) {

    if (!canEdit) return;

    if (!editForm.quantity) {
      alert("Quantity required");
      return;
    }

    //setSaving(true);
    setSavingLineId(lineId);

    try {

      await ipeEnquiryService.updateBOMLine(lineId, {
        quantity: Number(editForm.quantity),
        scrap_percent: Number(editForm.scrap_percent || 0),
        remarks: editForm.remarks,
        painting_required: editForm.painting_required,
        insulation_required: editForm.insulation_required,
        fabrication_required: editForm.fabrication_required,
      });
      // 🔥 OPEN SELECTOR FOR PROCESS MATERIAL

      const currentLine = lines.find(l => l.id === lineId);

      if (editForm.painting_required) {
        setChildProcessType("PAINT");
        setChildParentLine(currentLine);
        setShowSelector(true);
      }

      else if (editForm.insulation_required) {
        setChildProcessType("INSULATION");
        setChildParentLine(currentLine);
        setShowSelector(true);
      }

      setEditingLineId(null);

      onReload && onReload();

    } catch (err) {

      console.error("Failed to update BOM line", err);

    } finally {

      //setSaving(false);
      setSavingLineId(null);
    }

  }

  function cancelEdit() {
    setEditingLineId(null);
  }

  async function handleDelete(lineId) {

    if (!canEdit) return;

    if (!window.confirm("Delete this BOM line?")) return;

    try {

      await ipeEnquiryService.deleteBOMLine(lineId);
      onReload && onReload();

    } catch (err) {

      console.error("Delete failed", err);

    }
  }

  // 🔥 ORDER LINES (PARENT → CHILD)
const orderedLines = [];

lines
  .filter(l => !l.parent_line_id) // parent lines
  .forEach(parent => {

    orderedLines.push(parent);

    const children = lines.filter(
      c => c.parent_line_id === parent.id
    );

    orderedLines.push(...children);
  });
  // ------------------------------------------------------------
  // UI
  // ------------------------------------------------------------
  return (
    <div>

      {/* ================= ADD LINE ================= */}

      {bomId && canEdit && (

        <div className="mb-2">

          {!adding && (

            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setAdding(true)}
            >
              + Add BOM Line
            </button>

          )}

          {adding && (

            <div className="border rounded p-2 mt-2">

              <div className="row g-2">

                <div className="col-md-2">

                  <select
                    className="form-select form-select-sm"
                    value={form.material_type}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        material_type: e.target.value,
                        raw_material_id: "",
                        bought_out_item_id: "",
                        uom: "",
                      })
                    }
                  >
                    <option value="RAW">Raw Material</option>
                    <option value="BOUGHT_OUT">Bought Out</option>
                  </select>

                </div>

                {/* ✅ UPDATED MATERIAL SELECTOR */}
                <div className="col-md-3">

                  <div className="d-flex gap-1">

                    <input
                      className="form-control form-control-sm"
                      placeholder="Select Material"
                      value={form.raw_material_id ? "Material Selected" : ""}
                      readOnly
                    />

                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowSelector(true)}
                    >
                      🔍
                    </button>

                  </div>

                </div>

                <div className="col-md-2">

                  <input
                    className="form-control form-control-sm"
                    placeholder="UOM"
                    value={form.uom}
                    readOnly
                  />

                </div>

                <div className="col-md-2">

                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="Qty"
                    value={form.quantity}
                    onChange={(e) =>
                      setForm({ ...form, quantity: e.target.value })
                    }
                  />

                </div>

                <div className="col-md-3 d-flex gap-1">

                  <button
                    className="btn btn-sm btn-success"
                    disabled={saving}
                    //disabled={savingLineId === l.id}
                    onClick={handleAddLine}
                  >
                    Save
                  </button>

                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setAdding(false)}
                  >
                    Cancel
                  </button>

                </div>

              </div>

            </div>

          )}

        </div>

      )}

      {/* ================= TABLE ================= */}

      <table className="table table-bordered table-sm">

        <thead className="table-light">
          <tr>
            <th>SKU Code</th>
            <th>Material</th>
            <th>Category</th>
            <th>Manufacturer</th>
            <th>UOM</th>
            <th>Qty</th>
            <th>Scrap %</th>
            <th>Painting</th>
            <th>Insulation</th>
            <th>Fabrication</th>
            <th>Remarks</th>
            <th style={{ width: 120 }}>Action</th>
          </tr>
        </thead>

        <tbody>

          {lines.length === 0 && (
            <tr>
              <td colSpan="9" className="text-center text-muted">
                No BOM lines added
              </td>
            </tr>
          )}

          {/*{lines.map((l) => (*/}
          {orderedLines.map((l) => (
            <tr key={l.id}  style={
              l.parent_line_id
                ? { backgroundColor: "#eef6ff" } // light blue
                : {}
            }>

              {/* ✅ SKU CODE */}
              <td>{l.material_code || "-"}</td>

              {/* ✅ MATERIAL */}
              <td>
                  {l.parent_line_id && (
                    <span style={{ marginRight: 6 }}>↳</span>
                  )}
                  {resolveMaterialName(l)}
                </td>

              {/* ✅ CATEGORY */}
              <td>{l.category || "-"}</td>

              {/* ✅ MANUFACTURER */}
              <td>{l.manufacturer || "-"}</td>

              {/* UOM */}
              <td>{l.uom || "-"}</td>

              {editingLineId === l.id ? (

                <>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editForm.quantity}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          quantity: e.target.value,
                        })
                      }
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editForm.scrap_percent}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          scrap_percent: e.target.value,
                        })
                      }
                    />
                  </td>

                  {/* 🔥 Painting */}
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={editForm.painting_required ? "YES" : "NO"}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          painting_required: e.target.value === "YES",
                        })
                      }
                    >
                      <option value="NO">No</option>
                      <option value="YES">Yes</option>
                    </select>
                  </td>

                  {/* 🔥 Insulation */}
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={editForm.insulation_required ? "YES" : "NO"}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          insulation_required: e.target.value === "YES",
                        })
                      }
                    >
                      <option value="NO">No</option>
                      <option value="YES">Yes</option>
                    </select>
                  </td>

                  {/* 🔥 Fabrication */}
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={editForm.fabrication_required ? "YES" : "NO"}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          fabrication_required: e.target.value === "YES",
                        })
                      }
                    >
                      <option value="NO">No</option>
                      <option value="YES">Yes</option>
                    </select>
                  </td>
                  
                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={editForm.remarks}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          remarks: e.target.value,
                        })
                      }
                    />
                  </td>

                  <td>

                    <button
                      className="btn btn-sm btn-success me-1"
                      onClick={() => saveEdit(l.id)}
                      //disabled={saving}
                      disabled={savingLineId === l.id}
                    >
                      Save
                    </button>

                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={cancelEdit}
                    >
                      Cancel
                    </button>

                  </td>

                </>

              ) : (

                <>
                  <td>{l.quantity}</td>
                  <td>{l.scrap_percent || 0}</td>
                  

                  <td>{l.painting_required ? "Yes" : "No"}</td>
                  <td>{l.insulation_required ? "Yes" : "No"}</td>
                  <td>{l.fabrication_required ? "Yes" : "No"}</td>
                  <td>{l.remarks || "-"}</td>
                  <td>

                    {canEdit && (
                      <>
                        <button
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => startEdit(l)}
                        >
                          Edit
                        </button>

                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(l.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}

                  </td>

                </>

              )}

            </tr>

          ))}

        </tbody>

      </table>

      {/* ✅ MODAL */}
      <BOMMaterialSelectorModal
        show={showSelector}
        onClose={() => setShowSelector(false)}
        onConfirm={async (selected) => {

          if (!bomId || !canEdit) return;

          setSaving(true);

          // 🔥 CHILD MATERIAL FLOW
          if (childProcessType && childParentLine) {

            try {

              await Promise.all(
                selected.map((m) =>
                  ipeEnquiryService.createBOMLine({

                    bom_header_id: bomId,
                    material_type: "RAW",
                    raw_material_id: m.id,
                    uom_id: m.uom_id || null,
                    quantity: 1,
                    scrap_percent: 0,
                    remarks: "",

                    // 🔥 IMPORTANT
                    parent_line_id: childParentLine.id,
                    process_type: childProcessType,
                    is_auto_generated: true

                  })
                )
              );

              setChildProcessType(null);
              setChildParentLine(null);
              setShowSelector(false);

              onReload && onReload();

            } catch (err) {
              console.error("Child add failed", err);
              alert("Failed to add process material");
            }

            return; // 🚨 VERY IMPORTANT
          }
          try {

            // --------------------------------------------------------
            // 🔥 PREVENT DUPLICATES
            // --------------------------------------------------------
            const existingIds = lines
              .map(l => l.raw_material_id)
              .filter(Boolean);

            const filtered = selected.filter(
              m => !existingIds.includes(m.id)
            );

            if (filtered.length === 0) {
              alert("Selected materials already exist in BOM");
              setShowSelector(false);
              return;
            }

            // --------------------------------------------------------
            // 🔥 BATCH INSERT
            // --------------------------------------------------------
            await Promise.all(

              filtered.map((m) =>

                ipeEnquiryService.createBOMLine({

                  bom_header_id: bomId,

                  material_type: "RAW",

                  raw_material_id: m.id,

                  uom_id: m.uom_id || null,

                  quantity: 1, // default

                  scrap_percent: 0,

                  remarks: "",

                })

              )

            );

            // --------------------------------------------------------
            // RESET UI
            // --------------------------------------------------------
            setShowSelector(false);
            setAdding(false);

            onReload && onReload();

          } catch (err) {

            console.error("Batch add failed", err);
            alert("Failed to add selected materials");

          } finally {

            setSaving(false);

          }

        }}
      />

    </div>
  );
}